import { composeMomentVocabularies, createMomentCodec } from '@/_pure/directionMoment';
import { PRIMITIVE_MOMENT_VOCABULARY, stepMoment } from '@/adapters/primitive/_pure/derivation/primitiveMoment';
import vocabularyJson from '@/workflow-runner/_data/directions/momentVocabulary.json' with { type: 'json' };
import rodVocabularyJson from '@/workflow-runner/_data/directions/rodMomentVocabulary.json' with { type: 'json' };
import type { Moment, MomentVocabulary } from '@/_contract/_~generated/directionMomentSchema';
import type { MomentCodec } from '@/_contract/directionMoment';
import type { PrimitiveStepState } from '@/adapters/primitive/_contract/steps';
import type { TraceSnapshotView } from '@/workflow-runner/_contract/traceSnapshot';

/** Schema conformance is asserted by tests; JSON imports widen literal unions. */
export const WORKFLOW_MOMENT_VOCABULARY = vocabularyJson as unknown as MomentVocabulary;

/** The runner's own rod steps; embedded primitive operations use the primitive vocabulary extended by these. */
export const WORKFLOW_ROD_MOMENT_VOCABULARY = composeMomentVocabularies(
    PRIMITIVE_MOMENT_VOCABULARY,
    rodVocabularyJson as unknown as MomentVocabulary,
);

const ROD_MOMENT_CODEC = createMomentCodec(WORKFLOW_ROD_MOMENT_VOCABULARY);
const REPLAY_MOMENT_CODEC = createMomentCodec(WORKFLOW_MOMENT_VOCABULARY);

/** Encodes a workflow replay moment with the same grammar as primitive and rod moments. */
export function encodeWorkflowMoment(moment: Moment): string {
    const key = REPLAY_MOMENT_CODEC.encode(moment);
    if (moment.step.kind === 'tool-execution') {
        if (moment.step.parameters?.tracePoint === undefined) throw new Error('A tool-execution moment needs its tracePoint.');
        if (!moment.step.ordinal) throw new Error('A tool-execution moment needs its ordinal.');
    }
    return key;
}

export const WORKFLOW_MOMENT_CODEC: MomentCodec = { encode: encodeWorkflowMoment };

export function workflowExecutionMoment(tracePoint: number, index: number, count: number): Moment {
    return {
        frames: [],
        step: { kind: 'tool-execution', ordinal: { index, count }, parameters: { tracePoint } },
    };
}

/** Replay positions count Tool executions, not structural ticks or embedded primitive steps. */
export function deriveWorkflowCameraMoment(
    snapshot: TraceSnapshotView | undefined,
    replayPositionIndex = snapshot?.position.appliedReplayPositionIndex ?? 0,
): Moment {
    if (snapshot === undefined || replayPositionIndex === 0) return { frames: [], step: { kind: 'initial' } };
    const ticks = snapshot.tickTracePoints.filter(tick => tick.tracePointKind === 'tick'
        && tick.tickKind === 'tool_execution' && tick.stepKind === 'tool');
    const tick = ticks[replayPositionIndex - 1];
    if (!Number.isInteger(replayPositionIndex) || !tick) {
        throw new Error(`Workflow camera has no execution at replay position ${replayPositionIndex}.`);
    }
    return workflowExecutionMoment(tick.tracePointIndex, replayPositionIndex - 1, ticks.length);
}

/** The moment of a runner-owned rod step and its derived step key, for spreading into a step. */
export function rodStepMomentKey(kind: string): Pick<PrimitiveStepState, 'moment' | 'stepKey'> {
    const moment = stepMoment(kind);
    return { moment, stepKey: ROD_MOMENT_CODEC.encode(moment) };
}
